import { useQueryClient } from '@tanstack/react-query';
import { Alert, AlertDescription, AlertTitle, Button } from '@zond/ui';
import { AlertCircle, AlertTriangle, RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { openConnection, useDisconnectIntegration } from '../../integrations.queries';
import { isGithub } from './utils';

interface ConnectionAlertsProps {
  integrationName: string;
  onDisconnectSuccess: () => void;
}

export function ConnectionAlerts({ integrationName, onDisconnectSuccess }: ConnectionAlertsProps) {
  const { t } = useTranslation('integrations');
  const [isReconnecting, setIsReconnecting] = useState(false);
  const queryClient = useQueryClient();
  const isGithubIntegration = isGithub(integrationName);

  const { mutate: disconnect, isPending: isDisconnecting } = useDisconnectIntegration(
    () => {
      toast.success(t('connection.toast.disconnected', { name: integrationName }));
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      onDisconnectSuccess();
    },
    () => toast.error(t('connection.toast.disconnectFailed', { name: integrationName }))
  );

  const handleDisconnect = () => {
    disconnect(integrationName);
  };

  const handleReconnect = async () => {
    setIsReconnecting(true);
    try {
      await openConnection(integrationName);
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      await queryClient.invalidateQueries({ queryKey: ['integration-users', integrationName] });
      await queryClient.invalidateQueries({ queryKey: ['integration-projects', integrationName] });
      toast.success(t('connection.toast.reconnected', { name: integrationName }));
    } catch (error) {
      toast.error(t('connection.toast.reconnectFailed', { name: integrationName }));
    } finally {
      setIsReconnecting(false);
    }
  };

  return (
    <div className="px-6 pt-4 pb-6 space-y-4">
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{t('connection.reconnect.title')}</AlertTitle>
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>
            {isGithubIntegration
              ? t('connection.reconnect.descriptionGithub')
              : t('connection.reconnect.description')}
          </span>
          <Button
            variant="default"
            size="sm"
            onClick={handleReconnect}
            disabled={isReconnecting}
            className="gap-2 shrink-0"
          >
            <RefreshCw className="h-4 w-4" />
            {isReconnecting ? t('connection.reconnect.inProgress') : t('connection.reconnect.button')}
          </Button>
        </AlertDescription>
      </Alert>

      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>{t('connection.disconnect.title')}</AlertTitle>
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>{t('connection.disconnect.description', { name: integrationName })}</span>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDisconnect}
            disabled={isDisconnecting}
            className="gap-2 shrink-0"
          >
            <Trash2 className="h-4 w-4" />
            {isDisconnecting ? t('connection.disconnect.inProgress') : t('connection.disconnect.button')}
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  );
}
